<template>
  <div>
    <div class="summary-header">
      <div class="summary-title">
        <div class="summary-icon2"></div>
        <div class="summary-text">{{ $t('materials_request') }}</div>
      </div>
    </div>
    <DxDataGrid
      :data-source="store"
      :show-borders="false"
      :show-column-lines="false"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-auto-width="true"
      :show-column-headers="true"
      :show-colon="false"
      :remote-operations="false"
      :hover-state-enabled="false"
      :show-row-lines="false"
      :row-alternation-enabled="false"
      :no-data-text="$t('noDataText')"
      class="custom-grid custom-grid-material-request"
      @content-ready="init"
      @toolbar-preparing="onToolbarPreparing($event)"
    >
      <DxScrolling mode="standard" />

      <DxStateStoring
        :enabled="true"
        type="custom"
        storage-key="MaterialRequestTable"
        :custom-load="loadState"
        :custom-save="customSave"
      />

      <DxPaging :page-size="10" />

      <DxPager
        :show-page-size-selector="true"
        :allowed-page-sizes="[5, 10, 20]"
        :show-info="true"
        info-text="{2} items"
        :visible="true"
      />

      <DxSearchPanel
        :visible="true"
        :width="300"
        :placeholder="$t('searchRequestCode')"
      />

      <DxColumn
        data-field="CodSolicitud"
        :caption="$t('RequestCode')"
        :width="100"
        :allow-sorting="true"
        :fixed="true"
        fixed-position="left"
        :allow-header-filtering="false"
      />
      <DxColumn
        data-field="Referencia"
        :caption="$t('RequestReference')"
        :width="100"
        :allow-sorting="true"
        :fixed="true"
        fixed-position="left"
        :allow-header-filtering="false"
      />
      <DxColumn
        data-field="FechaSolicitud"
        :caption="$t('RequestDate')"
        :width="30"
        :allow-sorting="true"
        :fixed="true"
        alignment="center"
        fixed-position="left"
        :allow-header-filtering="false"
        data-type="date"
        :format="$helpers.getDateFormat(company)"
      />
      <DxColumn
        data-field="PlazoEntrega"
        :caption="$t('deliveryTime')"
        :width="30"
        :allow-sorting="true"
        :fixed="true"
        alignment="center"
        fixed-position="left"
        :allow-header-filtering="false"
      />
      <DxColumn
        data-field="TipoEntregaDescrp"
        :caption="$t('deliveryType')"
        :width="30"
        :allow-sorting="true"
        :fixed="true"
        alignment="center"
        fixed-position="left"
        :allow-header-filtering="false"
        cell-template="deliveryTypeTemplate"
      />
      <DxColumn
        data-field="Estado"
        :caption="$t('status')"
        :width="30"
        :fixed="true"
        alignment="center"
        fixed-position="right"
        :allow-header-filtering="false"
        cell-template="cellTemplateStatus"
      />

      <template #cellTemplateStatus="{ data }">
        <div
          :class="$helpers.getStatusClass(data.data.Estado)"
          style="text-align: center"
        >
          {{
            getTranslatedStatus(
              data.data.Estado,
              data.data.EstadoDescrp
            )
          }}
        </div>
      </template>
      <template #deliveryTypeTemplate="{ data }">
        <div style="text-align: center">
          {{
            getTranslatedDeliveryType(
              data.data.TipoEntrega,
              data.data.TipoEntregaDescrp
            )
          }}
        </div>
      </template>
    </DxDataGrid>
  </div>
</template>

<script>
import {
  DxDataGrid,
  DxColumn,
  DxSearchPanel,
  DxPager,
  DxPaging,
  DxScrolling,
  DxStateStoring,
} from 'devextreme-vue/data-grid';
import { mapActions, mapState } from 'vuex';
import { NOTIFY_TIME_LONG } from '@/js/constants';
import EventBus from '@/services/event-bus';

const MATERIAL_REQUEST_STATUS_FILTERS = [
  {
    key: 'pending',
    description: 'Pendiente de validaci\u00f3n',
    translationKey: 'materialRequest.pending',
    width: 220,
  },
  {
    key: 'rejected',
    description: 'Rechazada',
    translationKey: 'materialRequest.rejected',
    width: 150,
  },
  {
    key: 'accepted',
    description: 'Aceptada',
    translationKey: 'materialRequest.accepted',
    width: 150,
  },
  {
    key: 'inPreparation',
    description: 'En preparaci\u00f3n',
    translationKey: 'materialRequest.inPreparation',
    width: 170,
  },
  {
    key: 'onTheWay',
    description: 'Enviada',
    translationKey: 'materialRequest.onTheWay',
    width: 145,
  },
  {
    key: 'delivered',
    description: 'Entregada',
    translationKey: 'materialRequest.delivered',
    width: 155,
  },
];

export default {
  components: {
    DxDataGrid,
    DxColumn,
    DxSearchPanel,
    DxPager,
    DxPaging,
    DxScrolling,
    DxStateStoring,
  },
  props: {
    store: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      plazoEntrega: '24h - 72h',
      isFilterActive: {
        pending: false,
        rejected: false,
        accepted: false,
        inPreparation: false,
        onTheWay: false,
        delivered: false,
      },
    };
  },
  computed: {
    ...mapState('contact', ['company']),
    ...mapState('materialRequestFilters', [
      'materialStates',
      'tipoEnvio',
      'requestFromDate',
      'requestToDate',
    ]),
  },
  beforeMount() {
    const raw = localStorage.getItem('MaterialRequestTable');
    if (!raw) return;

    try {
      const saved = JSON.parse(raw);
      if (saved.isFilterActive) {
        Object.assign(this.isFilterActive, saved.isFilterActive);
      }
    } catch (e) {
      console.warn(
        'MaterialRequestTable corrupto en localStorage',
        e
      );
    }
  },
  async mounted() {
    await this.fetchMaterialFilterOptions();

    if (this.hasActiveStatusFilter()) {
      this.applyFilters();
    }
  },
  methods: {
    normalizeStatus(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    },
    getTranslatedStatus(statusId, description) {
      void statusId;
      const normalizedDescription = this.normalizeStatus(description);

      if (normalizedDescription === 'pendiente de validacion') {
        return this.$t('materialRequest.pending');
      }

      if (normalizedDescription === 'rechazada') {
        return this.$t('materialRequest.rejected');
      }

      if (normalizedDescription === 'aceptada') {
        return this.$t('materialRequest.accepted');
      }

      if (normalizedDescription === 'en preparacion') {
        return this.$t('materialRequest.inPreparation');
      }

      if (normalizedDescription === 'enviada') {
        return this.$t('materialRequest.onTheWay');
      }

      if (normalizedDescription === 'entregada') {
        return this.$t('materialRequest.delivered');
      }

      return description || '';
    },
    normalizeDeliveryType(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    },
    getTranslatedDeliveryType(typeId, description) {
      const normalizedDescription =
        this.normalizeDeliveryType(description);

      if (
        Number(typeId) === 1 ||
        normalizedDescription === 'recoge el cliente'
      ) {
        return this.$t('customerPickup');
      }

      if (Number(typeId) === 2 || normalizedDescription === 'envio') {
        return this.$t('shipping');
      }

      return description || '';
    },
    loadState() {
      let state = localStorage.getItem('MaterialRequestTable');
      if (state) {
        state = JSON.parse(state);
        delete state.isFilterActive;
        delete state.selectedMaterialRequestStatuses;
        state.searchText = '';
      }
      return state;
    },
    onToolbarPreparing(e) {
      const toolbarItems = e.toolbarOptions.items;
      e.toolbarOptions.elementAttr = {
        class: 'custom-toolbar',
      };
      toolbarItems.forEach((item) => {
        if (item.name === 'searchPanel') {
          item.location = 'before';
        }
      });

      // Botón Filtros (panel derecho)
      toolbarItems.push({
        name: 'Filtros',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('filter'),
          icon: '././static/icons/ic_filter.svg',
          width: 115,
          elementAttr: {
            class: 'btn-filtrar',
          },
          onClick: () => {
            this.$f7.panel.open('right');
          },
        },
      });
      // Botón Nueva Solicitud
      MATERIAL_REQUEST_STATUS_FILTERS.forEach((status) => {
        toolbarItems.push({
          name: status.key,
          location: 'before',
          widget: 'dxButton',
          locateInMenu: 'never',
          options: {
            text: this.$t(status.translationKey),
            icon: '././static/icons/ic_check.svg',
            width: status.width,
            elementAttr: {
              class: `filtrosBotones ${status.key} ${
                this.isFilterActive[status.key]
                  ? 'show-icon'
                  : 'hide-icon'
              }`,
            },
            onClick: (e) => {
              this.toggleStatusFilter(status.key);
              this.syncToolbarStatusButtons(e.element);
              this.applyFilters();
            },
          },
        });
      });

      toolbarItems.push({
        name: 'nueva solicitud',
        location: 'after',
        widget: 'dxButton',
        locateInMenu: 'never',
        cssClass: 'solicitar-material-row-2',
        options: {
          text: this.$t('Request_sampling_material'),
          elementAttr: {
            class: 'botones-registrar',
          },
          onClick: () => {
            this.GoMaterialRequester();
          },
        },
      });
    },
    toggleStatusFilter(statusKey) {
      const nextValue = !this.isFilterActive[statusKey];

      Object.keys(this.isFilterActive).forEach((key) => {
        this.isFilterActive[key] = false;
      });

      this.isFilterActive[statusKey] = nextValue;
    },
    syncToolbarStatusButtons(activeElement) {
      const toolbar = activeElement?.closest('.dx-toolbar');
      if (!toolbar) return;

      MATERIAL_REQUEST_STATUS_FILTERS.forEach((status) => {
        const button = toolbar.querySelector(
          `.dx-toolbar-item .filtrosBotones.${status.key}`
        );
        if (!button) return;

        button.classList.toggle(
          'show-icon',
          this.isFilterActive[status.key]
        );
        button.classList.toggle(
          'hide-icon',
          !this.isFilterActive[status.key]
        );
      });
    },
    hasActiveStatusFilter() {
      return Boolean(this.getActiveStatusFilter());
    },
    getActiveStatusFilter() {
      const activeKey = Object.keys(this.isFilterActive).find(
        (key) => this.isFilterActive[key]
      );

      return MATERIAL_REQUEST_STATUS_FILTERS.find(
        (status) => status.key === activeKey
      );
    },
    getSelectedDeliveryType() {
      return Array.isArray(this.tipoEnvio)
        ? this.tipoEnvio[0] ?? null
        : this.tipoEnvio ?? null;
    },
    getBackendStatusOption(statusFilter) {
      if (!statusFilter) return null;

      const normalizedDescription = this.normalizeStatus(
        statusFilter.description
      );
      const backendOption = (this.materialStates || []).find(
        (status) =>
          this.normalizeStatus(
            status.EstadoDescrp || status.state || status.descripcion
          ) === normalizedDescription
      );

      return (
        backendOption || {
          EstadoDescrp: statusFilter.description,
          state: statusFilter.description,
        }
      );
    },
    applyFilters() {
      const activeStatusFilter = this.getActiveStatusFilter();
      const backendStatus = this.getBackendStatusOption(
        activeStatusFilter
      );

      this.setSelectedStatuses(
        backendStatus?.id ? [String(backendStatus.id)] : []
      );

      EventBus.$emit('MaterialRequest', [
        backendStatus,
        this.getSelectedDeliveryType(),
        this.requestFromDate,
        this.requestToDate,
      ]);

      this.saveGridState();
    },
    saveGridState() {
      if (!this.grid) return;
      this.customSave(this.grid.state());
    },
    GoMaterialRequester() {
      try {
        this.$f7router.navigate(
          {
            name: 'MaterialRequester',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      }
    },
    customSave(state) {
      const extendedState = {
        ...state,
        isFilterActive: this.isFilterActive,
      };
      localStorage.setItem(
        'MaterialRequestTable',
        JSON.stringify(extendedState)
      );
    },
    init(e) {
      this.grid = e.component;
    },
    ...mapActions('materialRequestFilters', [
      'fetchMaterialFilterOptions',
      'setSelectedStatuses',
    ]),
  },
};
</script>
<style>
.custom-grid-material-request .custom-toolbar .dx-toolbar-before {
  width: 100%;
  padding-right: 0px !important;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(115px, max-content));
  grid-auto-rows: 48px;
  align-items: center;
  box-sizing: border-box;
}
.custom-grid-material-request
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(1) {
  grid-row: 1;
  grid-column: 1 / span 3;
}
.custom-grid-material-request
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(2) {
  grid-row: 2;
  grid-column: 1;
}
.custom-grid-material-request
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(3) {
  grid-row: 2;
  grid-column: 2;
}
.custom-grid-material-request
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(4) {
  grid-row: 2;
  grid-column: 3;
}
.custom-grid-material-request
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(5) {
  grid-row: 2;
  grid-column: 4;
}
.custom-grid-material-request
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(6) {
  grid-row: 2;
  grid-column: 5;
}
.custom-grid-material-request
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(7) {
  grid-row: 2;
  grid-column: 6;
}
.custom-grid-material-request
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(8) {
  grid-row: 2;
  grid-column: 7;
}

.custom-grid-material-request .custom-toolbar .dx-toolbar-after {
  display: grid !important;
  grid-template-columns: 1fr;
  grid-template-rows: 48px 48px;
  justify-items: end;
  align-items: center;
  box-sizing: border-box;
}
.custom-grid-material-request
  .custom-toolbar
  .dx-toolbar-after
  .solicitar-material-row-2 {
  grid-row: 2 !important;
}

.summary-header {
  margin: 12px;
  margin-bottom: 0px;
}
.summary-icon2 {
  width: 25px;
  height: 25px;
  background-image: url('../../static/icons/solicitar_material.svg');
}
</style>
